import { ActionIcon, Input, Menu, rem } from "@mantine/core";
import IconCardPlus from "@src/assets/icons/lucide/card-plus.svg?react";
import IconMessageSquare from "@src/assets/icons/lucide/message-square.svg?react";
import IconNotebookTabs from "@src/assets/icons/lucide/notebook-tabs.svg?react";
import IconPin from "@src/assets/icons/lucide/pin.svg?react";
import IconChevronRight from "@src/assets/icons/tabler/chevron-right.svg?react";
import IconCopy from "@src/assets/icons/tabler/copy.svg?react";
import IconDotsVertical from "@src/assets/icons/tabler/dots-vertical.svg?react";
import IconEye from "@src/assets/icons/tabler/eye.svg?react";
import IconFolderArrowRight from "@src/assets/icons/tabler/folder-arrow-right.svg?react";
import IconFolder from "@src/assets/icons/tabler/folder.svg?react";
import IconMoodSmile from "@src/assets/icons/tabler/mood-smile.svg?react";
import IconPencil from "@src/assets/icons/tabler/pencil.svg?react";
import IconPlus from "@src/assets/icons/tabler/plus.svg?react";
import IconStarFilled from "@src/assets/icons/tabler/star-filled.svg?react";
import IconStar from "@src/assets/icons/tabler/star.svg?react";
import IconTrash from "@src/assets/icons/tabler/trash.svg?react";
import IconUnlink from "@src/assets/icons/tabler/unlink.svg?react";
import IconUserEdit from "@src/assets/icons/tabler/user-edit.svg?react";
import IconUsers from "@src/assets/icons/tabler/users.svg?react";
import { ROUTES } from "@src/common/constants";
import {
	useArchiveChat,
	useRenameChat,
} from "@src/components/agent-chat/mutations/chat-mutations";
import { useBoardEvents } from "@src/components/board/board-events-context";
import { useNavbarFavourite } from "@src/components/board/mutations/favourite-navbar-item";
import { useNavbarRemoveChat } from "@src/components/board/mutations/remove-chat-navbar-item";
import { useRenameFolder } from "@src/components/board/mutations/rename-folder";
import { useRenameWhiteboard } from "@src/components/board/mutations/rename-whiteboard";
import { useNavbarUnfavourite } from "@src/components/board/mutations/unfavourite-navbar-item";
import {
	useWhiteboardNavigation,
	useWhiteboardRouteIdString,
} from "@src/components/board/use-whiteboard-navigation";
import { useRenameCard } from "@src/components/library/queries/card-operations";
import {
	type NavActiveSource,
	useNavActiveSource,
} from "@src/components/navigation/context/nav-active-source-context";
import { useNavBarHandlers } from "@src/components/navigation/context/nav-bar-handler-context";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import { NavbarSection, WhiteboardPermission } from "@src/gql/graphql";
import type { TreeItemRenderProps } from "@src/lib/clean-tree";
import { TreeDropIndicator } from "@src/lib/clean-tree/components/tree-drop-indicator";
import clsx from "clsx";
import {
	type ReactNode,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { EmojiPickerModal } from "./emoji-picker-modal";
import type {
	NavBoardTreeItem,
	NavCardTreeItem,
	NavChatTreeItem,
	NavFolderTreeItem,
	NavTreeItem,
} from "./nav-tree-item-model";
import { getTreeIndentPx } from "./tree-indent";
import { useNavTreeItemIcon } from "./use-nav-tree-item-icon";

function StarIcon({ filled }: { filled?: boolean }) {
	return filled ? (
		<IconStarFilled width={14} height={14} />
	) : (
		<IconStar width={14} height={14} />
	);
}

function StarOffIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="shrink-0"
			aria-hidden
		>
			<path d="M8.34 8.34 2 9.27l5 4.87L5.82 21 12 17.77 18.18 21l-.59-3.43" />
			<path d="M18.42 12.76 22 9.27l-6.91-1L12 2l-1.44 2.91" />
			<line x1="2" x2="22" y1="2" y2="22" />
		</svg>
	);
}

function BoardUnfavouriteButton({ itemKey }: { itemKey: string }) {
	const { mutateAsync: unfavourite } = useNavbarUnfavourite();
	return (
		<ActionIcon
			variant="subtle"
			size="xs"
			color="var(--ui-color-text-muted)"
			className="opacity-100 transition-opacity flex items-center justify-center"
			aria-label="Remove from favorites"
			title="Remove from favorites"
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
				unfavourite({ itemKey });
			}}
		>
			<StarOffIcon />
		</ActionIcon>
	);
}

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
	return (
		<IconChevronRight
			width={14}
			height={14}
			className={clsx(
				"shrink-0 transition-transform duration-150",
				isOpen && "rotate-90",
			)}
		/>
	);
}

const NAV_MENU_ATTR = "data-nav-menu";

/** Empty slot matching the folder chevron (14px). */
function TreeRowLeadingSlot() {
	return <span className="shrink-0 inline-block w-3.5 h-3.5" aria-hidden />;
}

/** 14×14 slot; nudge emoji glyphs that sit high on the baseline. */
function TreeRowEmoji({ emoji }: { emoji: string }) {
	return (
		<span
			className={clsx(
				"shrink-0 flex size-3.5 items-center justify-center",
				"overflow-hidden text-[14px] leading-none translate-y-px",
			)}
			aria-hidden
		>
			{emoji}
		</span>
	);
}

function isClickFromNavMenu(e: React.MouseEvent): boolean {
	const target = e.target;
	if (!(target instanceof Element)) {
		return false;
	}
	return target.closest?.(`[${NAV_MENU_ATTR}]`) != null;
}

interface FolderMenuProps {
	hasChildren: boolean;
	createBoardDisabled?: boolean;
	onCreateBoard?: () => void;
	onCreateCard?: () => void;
	onCreateChat?: () => void;
	onRename: () => void;
	onDelete: () => void;
	onChangeIcon: () => void;
}

function FolderMenu({
	hasChildren,
	createBoardDisabled,
	onCreateBoard,
	onCreateCard,
	onCreateChat,
	onRename,
	onDelete,
	onChangeIcon,
}: FolderMenuProps) {
	const hasCreateActions = Boolean(
		onCreateBoard || onCreateCard || onCreateChat,
	);
	return (
		<span {...{ [NAV_MENU_ATTR]: "" }}>
			<Menu
				shadow="md"
				radius="md"
				width={rem(180)}
				position="bottom-end"
				zIndex={9999}
			>
				<Menu.Target>
					<ActionIcon
						variant="subtle"
						size="xs"
						color="var(--ui-color-text-muted)"
						aria-label="Folder actions"
						title="Folder actions"
						className="opacity-100 transition-opacity flex items-center justify-center"
					>
						<IconDotsVertical width={14} height={14} />
					</ActionIcon>
				</Menu.Target>
				<Menu.Dropdown>
					{onCreateBoard && (
						<Menu.Item
							leftSection={<IconPlus width={14} height={14} />}
							disabled={createBoardDisabled}
							onClick={(e) => {
								e.stopPropagation();
								onCreateBoard();
							}}
						>
							New board
						</Menu.Item>
					)}
					{onCreateCard && (
						<Menu.Item
							leftSection={<IconCardPlus width={14} height={14} />}
							onClick={(e) => {
								e.stopPropagation();
								onCreateCard();
							}}
						>
							New card
						</Menu.Item>
					)}
					{onCreateChat && (
						<Menu.Item
							leftSection={<IconMessageSquare width={14} height={14} />}
							onClick={(e) => {
								e.stopPropagation();
								onCreateChat();
							}}
						>
							New chat
						</Menu.Item>
					)}
					{hasCreateActions && <Menu.Divider />}
					<Menu.Item
						leftSection={<IconPencil width={14} height={14} />}
						onClick={(e) => {
							e.stopPropagation();
							onRename();
						}}
					>
						Rename
					</Menu.Item>
					<Menu.Item
						leftSection={<IconMoodSmile width={14} height={14} />}
						onClick={(e) => {
							e.stopPropagation();
							onChangeIcon();
						}}
					>
						Change icon
					</Menu.Item>
					<Menu.Divider />
					<Tooltip
						label="Empty this folder or move items to delete"
						disabled={!hasChildren}
						position="right"
					>
						<Menu.Item
							leftSection={<IconTrash width={14} height={14} />}
							color="var(--ui-color-danger)"
							disabled={hasChildren}
							onClick={(e) => {
								e.stopPropagation();
								onDelete();
							}}
						>
							Delete
						</Menu.Item>
					</Tooltip>
				</Menu.Dropdown>
			</Menu>
		</span>
	);
}

interface BoardMenuProps {
	isFavourited: boolean;
	showRename: boolean;
	showDuplicate: boolean;
	showMoveToFolder: boolean;
	showArchive: boolean;
	showLeave?: boolean;
	isCard?: boolean;
	onRename: () => void;
	onDuplicate?: () => void;
	onMoveToFolder: () => void;
	onUnpinFromWorkspace?: () => void;
	onArchive: () => void;
	onLeave?: () => void;
	onFavourite: () => void;
	onUnfavourite: () => void;
	onChangeIcon?: () => void;
	onAddToCurrentBoard?: () => void;
}

function BoardMenu({
	isFavourited,
	showRename,
	showDuplicate,
	showMoveToFolder,
	showArchive,
	showLeave,
	isCard,
	onRename,
	onDuplicate,
	onMoveToFolder,
	onUnpinFromWorkspace,
	onArchive,
	onLeave,
	onFavourite,
	onUnfavourite,
	onChangeIcon,
	onAddToCurrentBoard,
}: BoardMenuProps) {
	return (
		<span {...{ [NAV_MENU_ATTR]: "" }}>
			<Menu
				shadow="md"
				radius="md"
				width={rem(180)}
				position="bottom-end"
				zIndex={9999}
			>
				<Menu.Target>
					<ActionIcon
						variant="subtle"
						size="xs"
						color="var(--ui-color-text-muted)"
						aria-label={isCard ? "Card actions" : "Board actions"}
						title={isCard ? "Card actions" : "Board actions"}
						className="opacity-100 transition-opacity flex items-center justify-center"
					>
						<IconDotsVertical width={14} height={14} />
					</ActionIcon>
				</Menu.Target>
				<Menu.Dropdown>
					{onAddToCurrentBoard && (
						<>
							<Menu.Item
								leftSection={<IconPlus width={14} height={14} />}
								onClick={(e) => {
									e.stopPropagation();
									onAddToCurrentBoard();
								}}
							>
								Add to current board
							</Menu.Item>
							<Menu.Divider />
						</>
					)}
					{isFavourited ? (
						<Menu.Item
							leftSection={<StarIcon filled />}
							onClick={(e) => {
								e.stopPropagation();
								onUnfavourite();
							}}
						>
							Remove from Favorites
						</Menu.Item>
					) : (
						<Menu.Item
							leftSection={<StarIcon />}
							onClick={(e) => {
								e.stopPropagation();
								onFavourite();
							}}
						>
							Add to Favorites
						</Menu.Item>
					)}
					{showRename && (
						<Menu.Item
							leftSection={<IconPencil width={14} height={14} />}
							onClick={(e) => {
								e.stopPropagation();
								onRename();
							}}
						>
							Rename
						</Menu.Item>
					)}
					{onChangeIcon && (
						<Menu.Item
							leftSection={<IconMoodSmile width={14} height={14} />}
							onClick={(e) => {
								e.stopPropagation();
								onChangeIcon();
							}}
						>
							Change icon
						</Menu.Item>
					)}
					{showDuplicate && (
						<Menu.Item
							leftSection={<IconCopy width={14} height={14} />}
							onClick={(e) => {
								e.stopPropagation();
								onDuplicate?.();
							}}
						>
							Duplicate
						</Menu.Item>
					)}
					{showMoveToFolder && (
						<Menu.Item
							leftSection={<IconFolderArrowRight width={14} height={14} />}
							onClick={(e) => {
								e.stopPropagation();
								onMoveToFolder();
							}}
						>
							Move to Folder…
						</Menu.Item>
					)}
					{onUnpinFromWorkspace && (
						<Menu.Item
							leftSection={<IconPin width={14} height={14} />}
							onClick={(e) => {
								e.stopPropagation();
								onUnpinFromWorkspace();
							}}
						>
							Unpin from Workspace
						</Menu.Item>
					)}
					{(showArchive || showLeave) && <Menu.Divider />}
					{showArchive && (
						<Menu.Item
							leftSection={<IconTrash width={14} height={14} />}
							color="var(--ui-color-danger)"
							onClick={(e) => {
								e.stopPropagation();
								onArchive();
							}}
						>
							{isCard ? "Remove from Workspace" : "Delete"}
						</Menu.Item>
					)}
					{showLeave && onLeave && (
						<Menu.Item
							leftSection={<IconUnlink width={14} height={14} />}
							color="var(--ui-color-danger)"
							onClick={(e) => {
								e.stopPropagation();
								onLeave();
							}}
						>
							Leave
						</Menu.Item>
					)}
				</Menu.Dropdown>
			</Menu>
		</span>
	);
}

function FolderNavItem({
	item,
	level,
	isDragging,
	isOpen,
	hasChildren,
	instruction,
	toggleOpen,
	dragHandleRef,
	createBoardDisabled,
	onCreateBoardInFolder,
	onCreateCardInFolder,
	onCreateChatInFolder,
	onDeleteFolder,
	navbarSection,
	autoRenameRequested,
	onAutoRenameConsumed,
}: {
	item: NavFolderTreeItem;
	level: number;
	isDragging: boolean;
	isOpen: boolean;
	hasChildren: boolean;
	instruction: TreeItemRenderProps["instruction"];
	toggleOpen: () => void;
	dragHandleRef: TreeItemRenderProps["dragHandleRef"];
	createBoardDisabled?: boolean;
	onCreateBoardInFolder?: (folderId: string) => void;
	onCreateCardInFolder?: (folderId: string) => void;
	onCreateChatInFolder?: (folderId: string) => void;
	onDeleteFolder?: (folderId: string) => void;
	navbarSection: NavbarSection;
	autoRenameRequested?: boolean;
	onAutoRenameConsumed?: (itemId: string) => void;
}) {
	const [isRenaming, setIsRenaming] = useState(false);
	const [name, setName] = useState(item.name);
	const inputRef = useRef<HTMLInputElement>(null);
	const { mutateAsync: renameFolder } = useRenameFolder();
	const {
		emoji,
		isEmojiPickerOpen,
		openEmojiPicker,
		closeEmojiPicker,
		handleSetEmoji,
		handleRemoveEmoji,
	} = useNavTreeItemIcon({
		section: navbarSection,
		itemKey: item.id,
		icon: item.icon,
	});

	const paddingStyle = useMemo(
		() => ({ paddingLeft: getTreeIndentPx(level) }),
		[level],
	);

	useEffect(() => {
		setName(item.name);
	}, [item.name]);

	useEffect(() => {
		if (!autoRenameRequested) {
			return;
		}
		setIsRenaming(true);
		onAutoRenameConsumed?.(item.id);
	}, [autoRenameRequested, item.id, onAutoRenameConsumed]);

	useEffect(() => {
		if (!isRenaming) {
			return;
		}
		const input = inputRef.current;
		if (!input) {
			return;
		}
		input.focus();
		input.select();
	}, [isRenaming]);

	const handleRename = useCallback(
		(value: string) => {
			const trimmed = value.trim();
			if (trimmed && trimmed !== item.name) {
				renameFolder({
					section: navbarSection,
					folderKey: item.id,
					newName: trimmed,
				});
				setName(trimmed);
			} else {
				setName(item.name);
			}
			setIsRenaming(false);
		},
		[item.id, item.name, navbarSection, renameFolder],
	);

	if (isRenaming) {
		return (
			<div className="relative" style={paddingStyle}>
				<Input
					ref={inputRef}
					value={name}
					size="xs"
					radius="md"
					classNames={{ input: "focus:border-brand-primary" }}
					autoFocus
					onChange={(e) => setName(e.currentTarget.value)}
					onBlur={(e) => handleRename(e.currentTarget.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							handleRename(e.currentTarget.value);
						}
						if (e.key === "Escape") {
							setName(item.name);
							setIsRenaming(false);
						}
					}}
				/>
			</div>
		);
	}

	return (
		<div className="relative">
			{/* biome-ignore lint/a11y/useSemanticElements: <button> would nest inside FolderMenu's ActionIcon <button> */}
			<div
				role="button"
				tabIndex={0}
				ref={dragHandleRef as React.RefObject<HTMLDivElement>}
				style={paddingStyle}
				onClick={(e) => {
					if (isClickFromNavMenu(e)) {
						return;
					}
					if (!hasChildren) {
						return;
					}
					toggleOpen();
				}}
				onKeyDown={(e) => {
					if (!hasChildren) {
						return;
					}
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						toggleOpen();
					}
				}}
				className={clsx(
					"group flex items-center gap-1.5 w-full rounded-lg pr-2 py-1 cursor-pointer",
					"hover:bg-surface-hover-subtle text-text-primary",
					"font-medium text-[13px] leading-snug select-none",
					isDragging && "opacity-50",
				)}
			>
				{hasChildren ? <ChevronIcon isOpen={isOpen} /> : <TreeRowLeadingSlot />}
				{emoji ? (
					<TreeRowEmoji emoji={emoji} />
				) : (
					<IconFolder width={14} height={14} className="shrink-0" />
				)}
				<span className="truncate flex-1 text-left">{item.name}</span>
				<FolderMenu
					hasChildren={hasChildren}
					createBoardDisabled={createBoardDisabled}
					onCreateBoard={
						onCreateBoardInFolder
							? () => {
									if (!isOpen) {
										toggleOpen();
									}
									onCreateBoardInFolder(item.id);
								}
							: undefined
					}
					onCreateCard={
						onCreateCardInFolder
							? () => {
									if (!isOpen) {
										toggleOpen();
									}
									onCreateCardInFolder(item.id);
								}
							: undefined
					}
					onCreateChat={
						onCreateChatInFolder
							? () => {
									if (!isOpen) {
										toggleOpen();
									}
									onCreateChatInFolder(item.id);
								}
							: undefined
					}
					onRename={() => setIsRenaming(true)}
					onDelete={() => onDeleteFolder?.(item.id)}
					onChangeIcon={openEmojiPicker}
				/>
			</div>
			{instruction && <TreeDropIndicator instruction={instruction} />}
			<EmojiPickerModal
				opened={isEmojiPickerOpen}
				onClose={closeEmojiPicker}
				currentEmoji={emoji}
				onSelect={handleSetEmoji}
				onRemove={handleRemoveEmoji}
				title="Choose folder icon"
			/>
		</div>
	);
}

const BoardNavItem = memo(function BoardNavItem({
	item,
	level,
	isDragging,
	isFavourited,
	instruction,
	dragHandleRef,
	onMoveToFolder,
	activeSource,
	navbarSection,
	showMoveToFolder,
	boardRowAction,
	autoRenameRequested,
	onAutoRenameConsumed,
}: {
	item: NavBoardTreeItem;
	level: number;
	isDragging: boolean;
	isFavourited: boolean;
	instruction: TreeItemRenderProps["instruction"];
	dragHandleRef: TreeItemRenderProps["dragHandleRef"];
	onMoveToFolder: (itemId: string) => void;
	activeSource: NavActiveSource;
	navbarSection: NavbarSection;
	showMoveToFolder: boolean;
	boardRowAction: "menu" | "unfavourite-only";
	autoRenameRequested?: boolean;
	onAutoRenameConsumed?: (itemId: string) => void;
}) {
	const { mutateAsync: renameWhiteboard } = useRenameWhiteboard();
	const {
		emoji,
		isEmojiPickerOpen,
		openEmojiPicker,
		closeEmojiPicker,
		handleSetEmoji,
		handleRemoveEmoji,
	} = useNavTreeItemIcon({
		section: navbarSection,
		itemKey: item.id,
		icon: item.icon,
	});

	const handleRename = useCallback(
		(value: string) => {
			const trimmed = value.trim();
			if (trimmed && trimmed !== item.name) {
				renameWhiteboard({ id: item.id, name: trimmed });
				setName(trimmed);
			} else {
				setName(item.name);
			}
			setIsRenaming(false);
		},
		[item.id, item.name, renameWhiteboard],
	);

	const { activeSource: currentActiveSource, setActiveSource } =
		useNavActiveSource();
	const { onArchive, onDuplicate, onLeave } = useNavBarHandlers();
	const { mutateAsync: favourite } = useNavbarFavourite();
	const { mutateAsync: unfavourite } = useNavbarUnfavourite();

	const paddingStyle = useMemo(
		() => ({ paddingLeft: getTreeIndentPx(level) }),
		[level],
	);

	const [isRenaming, setIsRenaming] = useState(false);
	const [name, setName] = useState(item.name);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setName(item.name);
	}, [item.name]);

	useEffect(() => {
		if (!autoRenameRequested) {
			return;
		}
		setIsRenaming(true);
		onAutoRenameConsumed?.(item.id);
	}, [autoRenameRequested, item.id, onAutoRenameConsumed]);

	useEffect(() => {
		if (!isRenaming) {
			return;
		}
		const input = inputRef.current;
		if (!input) {
			return;
		}
		input.focus();
		input.select();
	}, [isRenaming]);

	const { navigateToWhiteboard } = useWhiteboardNavigation();

	const isSharedWithMe = navbarSection === NavbarSection.SharedWithMe;
	const hasSharedWithMeWriteAccess =
		item.sharedWithMeListItemAccess === WhiteboardPermission.Write;
	const showRename = !isSharedWithMe || hasSharedWithMeWriteAccess;
	const showDuplicate = !isSharedWithMe || hasSharedWithMeWriteAccess;
	const showArchive = !isSharedWithMe;
	const showLeave = isSharedWithMe;

	if (isRenaming) {
		return (
			<div className="relative" style={paddingStyle}>
				<Input
					ref={inputRef}
					value={name}
					size="xs"
					radius="md"
					classNames={{ input: "focus:border-brand-primary" }}
					autoFocus
					onChange={(e) => setName(e.currentTarget.value)}
					onBlur={(e) => handleRename(e.currentTarget.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							handleRename(e.currentTarget.value);
						}
						if (e.key === "Escape") {
							setName(item.name);
							setIsRenaming(false);
						}
					}}
				/>
			</div>
		);
	}

	const whiteboardPath = `${ROUTES.whiteboards}/${item.whiteboardId.toIdString()}`;

	return (
		<div className="relative group">
			{/* DnD host cannot be <a href>: Chrome URL drags trigger split-view. */}
			<div
				ref={dragHandleRef as React.RefObject<HTMLDivElement>}
				className={clsx(
					"flex w-full items-center rounded-lg",
					isDragging && "opacity-50",
				)}
			>
				<NavLink
					to={whiteboardPath}
					draggable={false}
					onClick={(e) => {
						e.preventDefault();
						if (isClickFromNavMenu(e)) return;
						if (!isDragging) {
							setActiveSource(activeSource);
							navigateToWhiteboard(item.whiteboardId.toIdString());
						}
					}}
					style={paddingStyle}
					className={({ isActive }) =>
						clsx(
							"flex flex-1 min-w-0 items-center gap-1.5 rounded-lg pr-2 py-1",
							"cursor-pointer font-medium text-[13px] leading-snug select-none",
							isActive && currentActiveSource === activeSource
								? "bg-interaction-selected text-brand-primary shadow-[inset_4px_0_0_0_var(--ui-color-brand-primary)] hover:bg-interaction-selected-hover"
								: "hover:bg-surface-hover-subtle text-text-primary",
						)
					}
				>
					{({ isActive }) => {
						const isRowActive =
							isActive && currentActiveSource === activeSource;
						return (
							<>
								{item.sharedWithMeListItemAccess != null ? (
									<Tooltip
										label={
											item.sharedWithMeListItemAccess ===
											WhiteboardPermission.Write
												? "Editor access"
												: "View only"
										}
										openDelay={300}
										withArrow
									>
										<span className="shrink-0 inline-flex">
											{item.sharedWithMeListItemAccess ===
											WhiteboardPermission.Write ? (
												<IconUserEdit
													width={14}
													height={14}
													className={clsx(
														"shrink-0",
														isRowActive
															? "text-brand-primary"
															: "text-text-secondary",
													)}
													aria-hidden
												/>
											) : (
												<IconEye
													width={14}
													height={14}
													className={clsx(
														"shrink-0",
														isRowActive
															? "text-brand-primary"
															: "text-text-secondary",
													)}
													aria-hidden
												/>
											)}
										</span>
									</Tooltip>
								) : emoji ? (
									<TreeRowEmoji emoji={emoji} />
								) : item.isSharedOrPublished ? (
									<IconUsers
										width={14}
										height={14}
										className={clsx(
											"shrink-0",
											isRowActive
												? "text-brand-primary"
												: "text-text-secondary",
										)}
										aria-hidden
									/>
								) : (
									<TreeRowLeadingSlot />
								)}
								<span className="truncate flex-1 text-left">{item.name}</span>
								{boardRowAction === "menu" ? (
									<BoardMenu
										isFavourited={isFavourited}
										showRename={showRename}
										showDuplicate={showDuplicate}
										showMoveToFolder={showMoveToFolder}
										showArchive={showArchive}
										showLeave={showLeave}
										isCard={false}
										onChangeIcon={showRename ? openEmojiPicker : undefined}
										onRename={() => setIsRenaming(true)}
										onDuplicate={() => onDuplicate?.(item.id, item.name)}
										onMoveToFolder={() => onMoveToFolder(item.id)}
										onArchive={() => onArchive?.(item.id, "board")}
										onLeave={() => onLeave?.(item.id, "board")}
										onFavourite={() =>
											favourite({
												section: navbarSection,
												itemKey: item.id,
											})
										}
										onUnfavourite={() => unfavourite({ itemKey: item.id })}
									/>
								) : (
									<BoardUnfavouriteButton itemKey={item.id} />
								)}
							</>
						);
					}}
				</NavLink>
			</div>
			{instruction && <TreeDropIndicator instruction={instruction} />}
			<EmojiPickerModal
				opened={isEmojiPickerOpen}
				onClose={closeEmojiPicker}
				currentEmoji={emoji}
				onSelect={handleSetEmoji}
				onRemove={handleRemoveEmoji}
				title="Choose board icon"
			/>
		</div>
	);
});

const CardNavItem = memo(function CardNavItem({
	item,
	level,
	isDragging,
	isFavourited,
	instruction,
	dragHandleRef,
	onMoveToFolder,
	activeSource,
	navbarSection,
	showMoveToFolder,
	boardRowAction,
	autoRenameRequested,
	onAutoRenameConsumed,
}: {
	item: NavCardTreeItem;
	level: number;
	isDragging: boolean;
	isFavourited: boolean;
	instruction: TreeItemRenderProps["instruction"];
	dragHandleRef: TreeItemRenderProps["dragHandleRef"];
	onMoveToFolder: (itemId: string) => void;
	activeSource: NavActiveSource;
	navbarSection: NavbarSection;
	showMoveToFolder: boolean;
	boardRowAction: "menu" | "unfavourite-only";
	autoRenameRequested?: boolean;
	onAutoRenameConsumed?: (itemId: string) => void;
}) {
	const navigate = useNavigate();
	const { mutateAsync: renameCard } = useRenameCard();
	const {
		emoji,
		isEmojiPickerOpen,
		openEmojiPicker,
		closeEmojiPicker,
		handleSetEmoji,
		handleRemoveEmoji,
	} = useNavTreeItemIcon({
		section: navbarSection,
		itemKey: item.id,
		icon: item.icon,
	});

	const handleRename = useCallback(
		(value: string) => {
			const trimmed = value.trim();
			if (trimmed && trimmed !== item.name) {
				renameCard({ cardId: item.cardId.toString(), name: trimmed });
				setName(trimmed);
			} else {
				setName(item.name);
			}
			setIsRenaming(false);
		},
		[item.cardId, item.name, renameCard],
	);

	const { activeSource: currentActiveSource, setActiveSource } =
		useNavActiveSource();
	const { onArchive, onLeave } = useNavBarHandlers();
	const { mutateAsync: favourite } = useNavbarFavourite();
	const { mutateAsync: unfavourite } = useNavbarUnfavourite();
	const currentWhiteboardId = useWhiteboardRouteIdString();
	const { emit } = useBoardEvents();

	const isSharedWithMe = navbarSection === NavbarSection.SharedWithMe;
	const showArchive = navbarSection === NavbarSection.MyWhiteboards;
	const showLeave = isSharedWithMe;

	const paddingStyle = useMemo(
		() => ({ paddingLeft: getTreeIndentPx(level) }),
		[level],
	);

	const [isRenaming, setIsRenaming] = useState(false);
	const [name, setName] = useState(item.name);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setName(item.name);
	}, [item.name]);

	useEffect(() => {
		if (!autoRenameRequested) {
			return;
		}
		setIsRenaming(true);
		onAutoRenameConsumed?.(item.id);
	}, [autoRenameRequested, item.id, onAutoRenameConsumed]);

	useEffect(() => {
		if (!isRenaming) {
			return;
		}
		const input = inputRef.current;
		if (!input) {
			return;
		}
		input.focus();
		input.select();
	}, [isRenaming]);

	const cardPath = `/${item.cardId.toString()}`;

	if (isRenaming) {
		return (
			<div className="relative" style={paddingStyle}>
				<Input
					ref={inputRef}
					value={name}
					size="xs"
					radius="md"
					classNames={{ input: "focus:border-brand-primary" }}
					autoFocus
					onChange={(e) => setName(e.currentTarget.value)}
					onBlur={(e) => handleRename(e.currentTarget.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							handleRename(e.currentTarget.value);
						}
						if (e.key === "Escape") {
							setName(item.name);
							setIsRenaming(false);
						}
					}}
				/>
			</div>
		);
	}

	return (
		<div className="relative group">
			<div
				ref={dragHandleRef as React.RefObject<HTMLDivElement>}
				className={clsx(
					"flex w-full items-center rounded-lg",
					isDragging && "opacity-50",
				)}
			>
				<NavLink
					to={cardPath}
					draggable={false}
					onClick={(e) => {
						e.preventDefault();
						if (isClickFromNavMenu(e)) return;
						if (!isDragging) {
							setActiveSource(activeSource);
							navigate(cardPath);
						}
					}}
					style={paddingStyle}
					className={({ isActive }) =>
						clsx(
							"flex flex-1 min-w-0 items-center gap-1.5 rounded-lg pr-2 py-1",
							"cursor-pointer font-medium text-[13px] leading-snug select-none",
							isActive && currentActiveSource === activeSource
								? "bg-interaction-selected text-brand-primary shadow-[inset_4px_0_0_0_var(--ui-color-brand-primary)] hover:bg-interaction-selected-hover"
								: "hover:bg-surface-hover-subtle text-text-primary",
						)
					}
				>
					{({ isActive }) => {
						const isRowActive =
							isActive && currentActiveSource === activeSource;
						return (
							<>
								{item.sharedWithMeListItemAccess != null ? (
									<Tooltip
										label={
											item.sharedWithMeListItemAccess ===
											WhiteboardPermission.Write
												? "Editor access"
												: "View only"
										}
										openDelay={300}
										withArrow
									>
										<span className="shrink-0 inline-flex">
											{item.sharedWithMeListItemAccess ===
											WhiteboardPermission.Write ? (
												<IconUserEdit
													width={14}
													height={14}
													className={clsx(
														"shrink-0",
														isRowActive
															? "text-brand-primary"
															: "text-text-secondary",
													)}
													aria-hidden
												/>
											) : (
												<IconEye
													width={14}
													height={14}
													className={clsx(
														"shrink-0",
														isRowActive
															? "text-brand-primary"
															: "text-text-secondary",
													)}
													aria-hidden
												/>
											)}
										</span>
									</Tooltip>
								) : emoji ? (
									<TreeRowEmoji emoji={emoji} />
								) : (
									<IconNotebookTabs
										width={14}
										height={14}
										className={clsx(
											"shrink-0",
											isRowActive
												? "text-brand-primary"
												: "text-text-secondary",
										)}
										aria-hidden
									/>
								)}
								<span className="truncate flex-1 text-left">{item.name}</span>
								{boardRowAction === "menu" ? (
									<BoardMenu
										isFavourited={isFavourited}
										showRename={!isSharedWithMe}
										showDuplicate={false}
										showMoveToFolder={showMoveToFolder}
										showArchive={showArchive}
										showLeave={showLeave}
										isCard
										onAddToCurrentBoard={
											currentWhiteboardId
												? () =>
														emit({
															type: "place-card-on-board",
															payload: {
																cardEntityId: item.cardId.toString(),
															},
														})
												: undefined
										}
										onChangeIcon={!isSharedWithMe ? openEmojiPicker : undefined}
										onRename={() => setIsRenaming(true)}
										onMoveToFolder={() => onMoveToFolder(item.id)}
										onArchive={() => onArchive?.(item.id, "card")}
										onLeave={() => onLeave?.(item.id, "card")}
										onFavourite={() =>
											favourite({
												section: navbarSection,
												itemKey: item.id,
											})
										}
										onUnfavourite={() => unfavourite({ itemKey: item.id })}
									/>
								) : (
									<BoardUnfavouriteButton itemKey={item.id} />
								)}
							</>
						);
					}}
				</NavLink>
			</div>
			{instruction && <TreeDropIndicator instruction={instruction} />}
			<EmojiPickerModal
				opened={isEmojiPickerOpen}
				onClose={closeEmojiPicker}
				currentEmoji={emoji}
				onSelect={handleSetEmoji}
				onRemove={handleRemoveEmoji}
				title="Choose card icon"
			/>
		</div>
	);
});

const ChatNavItem = memo(function ChatNavItem({
	item,
	level,
	isDragging,
	isFavourited,
	instruction,
	dragHandleRef,
	onMoveToFolder,
	activeSource,
	navbarSection,
	showMoveToFolder,
	boardRowAction,
	autoRenameRequested,
	onAutoRenameConsumed,
}: {
	item: NavChatTreeItem;
	level: number;
	isDragging: boolean;
	isFavourited: boolean;
	instruction: TreeItemRenderProps["instruction"];
	dragHandleRef: TreeItemRenderProps["dragHandleRef"];
	onMoveToFolder: (itemId: string) => void;
	activeSource: NavActiveSource;
	navbarSection: NavbarSection;
	showMoveToFolder: boolean;
	boardRowAction: "menu" | "unfavourite-only";
	autoRenameRequested?: boolean;
	onAutoRenameConsumed?: (itemId: string) => void;
}) {
	const navigate = useNavigate();
	const location = useLocation();
	const { mutateAsync: renameChat } = useRenameChat();
	const { mutateAsync: archiveChat } = useArchiveChat();
	const { mutateAsync: unpinChat } = useNavbarRemoveChat();
	const {
		emoji,
		isEmojiPickerOpen,
		openEmojiPicker,
		closeEmojiPicker,
		handleSetEmoji,
		handleRemoveEmoji,
	} = useNavTreeItemIcon({
		section: navbarSection,
		itemKey: item.id,
		icon: item.icon,
	});

	const handleRename = useCallback(
		async (value: string) => {
			const trimmed = value.trim();
			if (trimmed && trimmed !== item.name) {
				await renameChat({ chatId: item.chatId.toIdString(), name: trimmed });
				setName(trimmed);
			} else {
				setName(item.name);
			}
			setIsRenaming(false);
		},
		[item.chatId, item.name, renameChat],
	);

	const { activeSource: currentActiveSource, setActiveSource } =
		useNavActiveSource();
	const { mutateAsync: favourite } = useNavbarFavourite();
	const { mutateAsync: unfavourite } = useNavbarUnfavourite();

	const isSharedWithMe = navbarSection === NavbarSection.SharedWithMe;
	const showArchive = navbarSection === NavbarSection.MyWhiteboards;

	const paddingStyle = useMemo(
		() => ({ paddingLeft: getTreeIndentPx(level) }),
		[level],
	);

	const [isRenaming, setIsRenaming] = useState(false);
	const [name, setName] = useState(item.name);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setName(item.name);
	}, [item.name]);

	useEffect(() => {
		if (!autoRenameRequested) {
			return;
		}
		setIsRenaming(true);
		onAutoRenameConsumed?.(item.id);
	}, [autoRenameRequested, item.id, onAutoRenameConsumed]);

	useEffect(() => {
		if (!isRenaming) {
			return;
		}
		const input = inputRef.current;
		if (!input) {
			return;
		}
		input.focus();
		input.select();
	}, [isRenaming]);

	const chatPath = `/${item.chatId.toString()}`;

	if (isRenaming) {
		return (
			<div className="relative" style={paddingStyle}>
				<Input
					ref={inputRef}
					value={name}
					size="xs"
					radius="md"
					classNames={{ input: "focus:border-brand-primary" }}
					autoFocus
					onChange={(e) => setName(e.currentTarget.value)}
					onBlur={(e) => void handleRename(e.currentTarget.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							void handleRename(e.currentTarget.value);
						}
						if (e.key === "Escape") {
							setName(item.name);
							setIsRenaming(false);
						}
					}}
				/>
			</div>
		);
	}

	return (
		<div className="relative group">
			<div
				ref={dragHandleRef as React.RefObject<HTMLDivElement>}
				className={clsx(
					"flex w-full items-center rounded-lg",
					isDragging && "opacity-50",
				)}
			>
				<NavLink
					to={chatPath}
					draggable={false}
					onClick={(e) => {
						e.preventDefault();
						if (isClickFromNavMenu(e)) return;
						if (!isDragging) {
							setActiveSource(activeSource);
							navigate(chatPath);
						}
					}}
					style={paddingStyle}
					className={({ isActive }) =>
						clsx(
							"flex flex-1 min-w-0 items-center gap-1.5 rounded-lg pr-2 py-1",
							"cursor-pointer font-medium text-[13px] leading-snug select-none",
							isActive && currentActiveSource === activeSource
								? "bg-interaction-selected text-brand-primary shadow-[inset_4px_0_0_0_var(--ui-color-brand-primary)] hover:bg-interaction-selected-hover"
								: "hover:bg-surface-hover-subtle text-text-primary",
						)
					}
				>
					{({ isActive }) => {
						const isRowActive =
							isActive && currentActiveSource === activeSource;
						return (
							<>
								{emoji ? (
									<TreeRowEmoji emoji={emoji} />
								) : (
									<IconMessageSquare
										width={14}
										height={14}
										className={clsx(
											"shrink-0",
											isRowActive
												? "text-brand-primary"
												: "text-text-secondary",
										)}
										aria-hidden
									/>
								)}
								<span className="truncate flex-1 text-left">{item.name}</span>
								{boardRowAction === "menu" ? (
									<BoardMenu
										isFavourited={isFavourited}
										showRename={!isSharedWithMe}
										showDuplicate={false}
										showMoveToFolder={showMoveToFolder}
										showArchive={showArchive}
										showLeave={false}
										isCard={false}
										onChangeIcon={!isSharedWithMe ? openEmojiPicker : undefined}
										onRename={() => setIsRenaming(true)}
										onMoveToFolder={() => onMoveToFolder(item.id)}
										onUnpinFromWorkspace={
											showArchive
												? () =>
														void unpinChat({
															section: NavbarSection.MyWhiteboards,
															chatId: item.chatId.toString(),
														})
												: undefined
										}
										onArchive={async () => {
											await archiveChat({ chatId: item.chatId.toIdString() });
											if (location.pathname === chatPath) {
												navigate(ROUTES.ask);
											}
										}}
										onFavourite={() =>
											favourite({
												section: navbarSection,
												itemKey: item.id,
											})
										}
										onUnfavourite={() => unfavourite({ itemKey: item.id })}
									/>
								) : (
									<BoardUnfavouriteButton itemKey={item.id} />
								)}
							</>
						);
					}}
				</NavLink>
			</div>
			{instruction && <TreeDropIndicator instruction={instruction} />}
			<EmojiPickerModal
				opened={isEmojiPickerOpen}
				onClose={closeEmojiPicker}
				currentEmoji={emoji}
				onSelect={handleSetEmoji}
				onRemove={handleRemoveEmoji}
				title="Choose chat icon"
			/>
		</div>
	);
});

export type NavTreeItemExtraProps = {
	favouriteKeys: Set<string>;
	createBoardDisabled?: boolean;
	onCreateBoardInFolder?: (folderId: string) => void;
	onCreateCardInFolder?: (folderId: string) => void;
	onCreateChatInFolder?: (folderId: string) => void;
	onDeleteFolder?: (folderId: string) => void;
	onMoveToFolder: (itemId: string) => void;
	activeSource: NavActiveSource;
	/** Mutations that scope by navbar list (defaults to My Boards). */
	navbarSection?: NavbarSection;
	showMoveToFolder?: boolean;
	/** Favorites tree: star-off control only (no board ⋮ menu). */
	boardRowAction?: "menu" | "unfavourite-only";
	pendingRenameItemId?: string | null;
	onPendingRenameHandled?: (itemId: string) => void;
};

function NavTreeItemRenderer(
	props: TreeItemRenderProps & NavTreeItemExtraProps,
): ReactNode {
	const {
		item: treeItem,
		level,
		state,
		instruction,
		isOpen,
		hasChildren,
		toggleOpen,
		dragHandleRef,
		favouriteKeys,
		createBoardDisabled,
		onCreateBoardInFolder,
		onCreateCardInFolder,
		onCreateChatInFolder,
		onDeleteFolder,
		onMoveToFolder,
		activeSource,
		navbarSection = NavbarSection.MyWhiteboards,
		showMoveToFolder = true,
		boardRowAction = "menu",
		pendingRenameItemId,
		onPendingRenameHandled,
	} = props;
	const item = treeItem as NavTreeItem;
	const isDragging = state === "dragging";
	const isFavourited =
		favouriteKeys.has(item.id) || navbarSection === NavbarSection.Favourites;
	const autoRenameRequested = pendingRenameItemId === item.id;

	if (item.kind === "folder") {
		return (
			<FolderNavItem
				item={item}
				level={level}
				isDragging={isDragging}
				isOpen={isOpen}
				hasChildren={hasChildren}
				instruction={instruction}
				toggleOpen={toggleOpen}
				dragHandleRef={dragHandleRef}
				createBoardDisabled={createBoardDisabled}
				onCreateBoardInFolder={onCreateBoardInFolder}
				onCreateCardInFolder={onCreateCardInFolder}
				onCreateChatInFolder={onCreateChatInFolder}
				onDeleteFolder={onDeleteFolder}
				navbarSection={navbarSection}
				autoRenameRequested={autoRenameRequested}
				onAutoRenameConsumed={onPendingRenameHandled}
			/>
		);
	}

	if (item.kind === "card") {
		return (
			<CardNavItem
				item={item}
				level={level}
				isDragging={isDragging}
				isFavourited={isFavourited}
				instruction={instruction}
				dragHandleRef={dragHandleRef}
				onMoveToFolder={onMoveToFolder}
				activeSource={activeSource}
				navbarSection={navbarSection}
				showMoveToFolder={showMoveToFolder}
				boardRowAction={boardRowAction}
				autoRenameRequested={autoRenameRequested}
				onAutoRenameConsumed={onPendingRenameHandled}
			/>
		);
	}

	if (item.kind === "chat") {
		return (
			<ChatNavItem
				item={item}
				level={level}
				isDragging={isDragging}
				isFavourited={isFavourited}
				instruction={instruction}
				dragHandleRef={dragHandleRef}
				onMoveToFolder={onMoveToFolder}
				activeSource={activeSource}
				navbarSection={navbarSection}
				showMoveToFolder={showMoveToFolder}
				boardRowAction={boardRowAction}
				autoRenameRequested={autoRenameRequested}
				onAutoRenameConsumed={onPendingRenameHandled}
			/>
		);
	}

	return (
		<BoardNavItem
			item={item}
			level={level}
			isDragging={isDragging}
			isFavourited={isFavourited}
			instruction={instruction}
			dragHandleRef={dragHandleRef}
			onMoveToFolder={onMoveToFolder}
			activeSource={activeSource}
			navbarSection={navbarSection}
			showMoveToFolder={showMoveToFolder}
			boardRowAction={boardRowAction}
			autoRenameRequested={autoRenameRequested}
			onAutoRenameConsumed={onPendingRenameHandled}
		/>
	);
}

export function createNavTreeItemRenderer(extra: NavTreeItemExtraProps) {
	return (props: TreeItemRenderProps): ReactNode => {
		return <NavTreeItemRenderer {...props} {...extra} />;
	};
}
